import { effect, Signal } from '@angular/core';
import {
  patchState,
  signalStoreFeature,
  withHooks,
  withMethods,
} from '@ngrx/signals';

/**
 * Redux DevTools Extension interface
 */
interface ReduxDevToolsExtension {
  connect(options?: { name?: string }): {
    init(state: unknown): void;
    send(action: string | { type: string }, state: unknown): void;
    subscribe(listener: (message: unknown) => void): () => void;
  };
}

declare global {
  interface Window {
    __REDUX_DEVTOOLS_EXTENSION__?: ReduxDevToolsExtension;
  }
}

/**
 * Custom NgRx SignalStore feature for Redux DevTools integration
 * Logs all state changes to Redux DevTools browser extension
 *
 * @param config Configuration options
 * @returns SignalStore feature
 *
 * @example
 * ```typescript
 * export const MyStore = signalStore(
 *   withState(initialState),
 *   withDevtools({ name: 'MyStore' }),
 *   withMethods(...),
 * );
 * ```
 */
export function withDevtools<State extends object>(config?: {
  name?: string;
  disabled?: boolean;
}) {
  return signalStoreFeature(
    withMethods((store) => {
      const devtools =
        typeof window !== 'undefined'
          ? window.__REDUX_DEVTOOLS_EXTENSION__?.connect({
              name: config?.name || 'NgRx SignalStore',
            })
          : null;

      return {
        /**
         * Log an action to Redux DevTools
         * Call this from your methods to track state changes
         */
        _logAction(actionName: string, before?: unknown, after?: unknown) {
          if (config?.disabled || !devtools) return;

          const snapshot: Record<string, unknown> = {};
          const storeObj = store as unknown as Record<string, unknown>;

          // Extract all signal values for DevTools
          Object.keys(storeObj).forEach((key) => {
            if (typeof storeObj[key] === 'function' && !key.startsWith('_')) {
              try {
                snapshot[key] = (storeObj[key] as Signal<unknown>)();
              } catch {
                // Skip non-signal properties
              }
            }
          });

          devtools.send({ type: actionName }, snapshot);
        },
      };
    }),
    withHooks({
      onInit(store) {
        if (config?.disabled) return;

        const devtools =
          typeof window !== 'undefined'
            ? window.__REDUX_DEVTOOLS_EXTENSION__?.connect({
                name: config?.name || 'NgRx SignalStore',
              })
            : null;

        if (devtools) {
          // Helper to extract state snapshot
          const getSnapshot = (): Record<string, unknown> => {
            const snapshot: Record<string, unknown> = {};
            const storeObj = store as unknown as Record<string, unknown>;

            Object.keys(storeObj).forEach((key) => {
              if (typeof storeObj[key] === 'function' && !key.startsWith('_')) {
                try {
                  snapshot[key] = (storeObj[key] as Signal<unknown>)();
                } catch {
                  // Skip non-signal properties
                }
              }
            });

            return snapshot;
          };

          // Send initial state
          devtools.init(getSnapshot());

          // Auto-track state changes with effect
          effect(() => {
            devtools.send({ type: 'STATE_CHANGE' }, getSnapshot());
          });
        }
      },
    }),
  );
}
